<template>
  <BTooltip title="Print" v-bind="$attrs">
    <button
      v-if="printOption"
      type="button"
      :class="['btn btn-flat', {'btn-sm':small}]"
      :disabled="printing"
      @click="showModal = true"
    >
      <i v-if="!printing" class="bi-printer" />
      <spinner-view v-else small />
    </button>
    <template v-else>
      <button
        type="button"
        ref="button"
        class="btn btn-flat dropdown-toggle no-caret"
        data-bs-toggle="dropdown"
        :disabled="printing"
      >
        <i v-if="!printing" class="bi-printer" />
        <spinner-view v-else small />
      </button>
      <ul class="dropdown-menu position-fixed">
        <li v-for="option in printOptions" :key="option.label">
          <button type="button" @click="selectAndShowModal(option)" class="dropdown-item">
            {{ option.label }}
          </button>
        </li>
      </ul>
    </template>
  </BTooltip>

  <LetterWrapper
    :model="context"
    ref="letterContent"
    class="print"
  />

  <ModalView
    v-model="showModal"
    title="Print Letter"
    ok-title="Print"
    :busy="printing"
    @ok="confirmPrint"
  >
    <div class="d-flex flex-column gap-3">
      <div v-if="printError" class="alert alert-danger" role="alert">{{ printError }}</div>
      <!-- Letter description card -->
      <div class="print-letter-card">
        <div class="fw-semibold">{{ effectiveLabel }}</div>
        <div v-if="context?.ownerFormatted" class="mt-1">{{ context.ownerFormatted }}</div>
        <div v-for="(line, i) in mailingAddressLines" :key="i" class="text-muted">{{ line }}</div>
      </div>

      <!-- Attachment section -->
      <div>
        <div class="section-label mb-2">Attachment</div>
        <CheckboxCard
          v-model="includeScreenshot"
          label="Include screenshot"
          sublabel="Attach a screenshot of the listing as evidence"
          :disabled="!isEvidenceCaptured"
        >
          <div v-if="showModal && isEvidenceCaptured" :class="['screenshot-preview', { 'screenshot-unchecked': !includeScreenshot }]">
            <PrivateEvidenceImage
              :city-id="context?.cityId"
              :listing-id="context?.listingId"
              :evidence="context?.evidence"
            />
          </div>
        </CheckboxCard>
      </div>

      <!-- Evidence status (shown when screenshot not captured) -->
      <EvidenceStatus
        v-if="!isEvidenceCaptured"
        :evidence="context?.evidence"
        :city-id="context?.cityId"
        :listing-id="context?.listingId"
        @updated="$emit('evidence-updated')"
      />
    </div>
  </ModalView>
</template>

<script>
import LetterWrapper from "@/components/letters/LetterWrapper.vue";
import SpinnerView from "@/components/common/SpinnerView.vue";
import BTooltip from "@/components/common/BTooltip.vue";
import ModalView from "@/components/common/ModalView.vue";
import EvidenceStatus from "@/components/letters/EvidenceStatus.vue";
import CheckboxCard from "@/components/common/CheckboxCard.vue";
import {LetterContext} from "@/models/letterContext";
import PrivateEvidenceImage from './PrivateEvidenceImage.vue';
export default {
  components: {BTooltip, SpinnerView, LetterWrapper, ModalView, EvidenceStatus, CheckboxCard, PrivateEvidenceImage},
  props: {
    context: LetterContext,
    printOption: {
      type: Object
    },
    label: {
      type: String,
      default: null
    },
    small: Boolean
  },
  emits: ['printed', 'evidence-updated'],
  data() {
    return {
      printing: false,
      printError: null,
      showModal: false,
      includeScreenshot: false,
      selectedOption: null
    };
  },
  computed: {
    printOptions() {
      return this.context?.printOptions || [];
    },
    isEvidenceCaptured() {
      const status = this.context?.evidence?.status;
      return status === 'captured' || status === 'manual_upload';
    },
    effectiveLabel() {
      if (this.selectedOption?.label) return this.selectedOption.label;
      if (this.label) return this.label;
      if (this.printOption) {
        const match = this.printOptions.find(
          o => o.template === this.printOption.template
        );
        return match?.label || 'Letter';
      }
      return 'Letter';
    },
    mailingAddressLines() {
      return this.context?.ownerAddressLines || [];
    }
  },
  watch: {
    showModal(visible) {
      if (visible) {
        this.printError = null;
        this.includeScreenshot = this.isEvidenceCaptured;
      } else if (this.printing) {
        this.$refs.letterContent.cancelPrint();
      }
    }
  },
  methods: {
    openPrintDialog() {
      this.printError = null;
      this.showModal = true;
    },
    selectAndShowModal(option) {
      this.selectedOption = option;
      this.showModal = true;
    },
    async confirmPrint() {
      const option = this.selectedOption || this.printOption;
      if (!option) return;
      this.printError = null;
      this.printing = true;
      try {
        await this.$refs.letterContent.print(option.template, option.options, this.includeScreenshot);
        this.showModal = false;
        this.$emit('printed', option);
        this.selectedOption = null;
      } catch {
        this.printError = 'Could not prepare the letter for printing. Try again, or deselect the screenshot to print without it.';
      } finally {
        this.printing = false;
      }

      if (this.$refs.button) {
        this.bootstrap.Dropdown.getInstance(this.$refs.button)?.hide();
      }
    }
  }
}
</script>

<style scoped>
.print-letter-card {
  background: var(--bs-light);
  border: 1px solid var(--bs-border-color);
  border-radius: 7px;
  padding: 12px 16px;
  font-size: 0.875rem;
}
.screenshot-preview {
  margin-top: 6px;
  transition: opacity 0.15s;
}
.screenshot-preview :deep(img) {
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
}
.screenshot-unchecked {
  opacity: 0.35;
}
</style>
